import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Package, FileText, User, MapPin,  AlertCircle, Pencil } from 'lucide-react';
import { PROPACKS_IVR_GRID_OPTIONS, getCompleteProductList } from '../../../data/conservativeCareDashboard';

export default function ProPacksOrderSummary({ formData, patient, provider, onConfirm, loading, error, onEdit, isPartialOrder }) {
  
  const selectedKit = formData.selectedKitOption 
    ? getCompleteProductList(formData.selectedKitOption)
    : null;

  if (!selectedKit) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>No kit selected</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-black mb-2">ProPacks Order Summary</h2>
        <p className="text-blue-100">
          {isPartialOrder 
            ? "Complete your pending order" 
            : "Review your order details before final submission"}
        </p>
      </div>

       {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border-2 border-red-300 rounded-2xl p-5 flex items-start gap-4"
          >
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-red-800 font-black text-sm mb-1">Order Submission Failed</p>
              <p className="text-red-700 text-sm font-medium whitespace-pre-line">{error}</p>
              <button
                onClick={onEdit}
                className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-red-700 hover:text-red-900 underline underline-offset-2 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit order to fix the issue
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Patient Info */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Patient Information</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 font-semibold">Name</p>
            <p className="text-gray-900">{formData.patientName}</p>
          </div>
          <div>
            <p className="text-gray-600 font-semibold">Date of Birth</p>
            <p className="text-gray-900">{formData.dob}</p>
          </div>
          <div>
            <p className="text-gray-600 font-semibold">Primary Insurance</p>
            <p className="text-gray-900">{formData.primaryInsurance || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-600 font-semibold">Policy Number</p>
            <p className="text-gray-900">{formData.primaryPolicyNumber || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Wound Details */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Wound Information</h3>
        </div>
        <div className="space-y-4">
          {formData.wounds.filter(w => w.icd10Code).map((wound, idx) => (
            <div key={idx} className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50 rounded">
              <p className="font-bold text-sm text-purple-900">Wound #{wound.woundNumber}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                <div>
                  <p className="text-gray-600">ICD-10:</p>
                  <p className="text-gray-900 font-semibold">{wound.icd10Code}</p>
                </div>
                <div>
                  <p className="text-gray-600">Location:</p>
                  <p className="text-gray-900">{wound.woundLocation || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Size:</p>
                  <p className="text-gray-900">{wound.length}cm × {wound.width}cm</p>
                </div>
                <div>
                  <p className="text-gray-600">Exudate:</p>
                  <p className="text-gray-900 capitalize">{wound.exudateLevel}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Kit */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Package className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Selected ProPack Kit</h3>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
          <p className="font-black text-green-900 text-lg mb-2">{selectedKit.category}</p>
          <p className="text-green-800 font-semibold mb-4">{selectedKit.displayName}</p>
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-700">Included Products:</p>
            {selectedKit.products.map((product, idx) => (
              <div key={idx} className="flex justify-between text-sm bg-white rounded px-3 py-2">
                <span className="text-gray-900">{product.description}</span>
                <span className="font-semibold text-green-700">{product.code} ({product.units} units)</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-800 font-semibold">Delivery Date</p>
            <p className="text-blue-900 font-bold">{formData.surgeryDate}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-800 font-semibold">Duration</p>
            <p className="text-blue-900 font-bold">{formData.lengthOfNeed} month(s)</p>
          </div>
        </div>
      </div>

      {/* Delivery Address */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <MapPin className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Delivery Address</h3>
        </div>
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
          <p className="font-semibold text-gray-900">{formData.patientName}</p>
          <p className="text-gray-700">{formData.address}</p>
          <p className="text-gray-700">{formData.city}, {formData.state} {formData.zip}</p>
        </div>
      </div>

      {/* Confirm Button */}
    {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {onEdit && (
            <motion.button
                onClick={onEdit}
                disabled={loading}
                className="flex-1 px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-2xl font-black text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2 border-gray-300 flex items-center justify-center gap-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <Pencil className="w-5 h-5" />
                Edit Order
            </motion.button>
        )}

        <motion.button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-2xl font-black text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <>
              <motion.div
                className="w-6 h-6 border-4 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              Creating Order...
            </>
          ) : (
            <>
              <Check className="w-6 h-6" />
              {isPartialOrder ? "Complete & Submit Order" : "Confirm & Submit Order"}
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}